import type { TextStreamPart } from "ai";

import type { generateToolSet } from "@builder/assist/services/tools/toolset";
import type { generateDesignAIToolset } from "@builder/assist/services/tools/DesignAI/designAIToolset";

// Collect the names of all available tools from both main and design AI toolsets.
type MainToolNames = keyof ReturnType<typeof generateToolSet>;
type DesignToolNames = keyof ReturnType<typeof generateDesignAIToolset>;
export type AvailableToolName = MainToolNames | DesignToolNames;

export type stepTextToShow = {
    present: string;
    past: string;
};

export const toolCallToText: Partial<Record<AvailableToolName, (step: TextStreamPart<any>) => stepTextToShow>> = {
    seeJSXOfLayer: () => {
        return { present: `Understanding current page`, past: `Understood current page` };
    },
    renamePage: step => {
        const { name } = (step as any).args ?? {};

        if (!name) {
            return { present: `Attempting to rename page`, past: `Tried renaming page` };
        }
        return { present: `Renaming page to ${name}`, past: `Renamed page to ${name}` };
    },
    // Design-AI specific tools
    getColorScheme: () => {
        return { present: `Reading color scheme`, past: `Read color scheme` };
    },
    addColorToColorScheme: step => {
        console.log("addColorToColorScheme STEP:", step);
        const { color, name } = (step as any).args ?? {};
        if (!color || !name) {
            return { present: `Adding a color`, past: `Added a color` };
        }
        return { present: `Adding ${name} to color scheme (${color})`, past: `Added ${name} to color scheme (${color})` };
    },
    injectJSX: step => {
        try {
            const { jsx } = (step as any).args ?? {};
            const approxNumOfElements = (jsx?.split("<").length || 0) - 1;
            if (approxNumOfElements < 50) {
                return { present: `Editing some elements`, past: `Edited some elements` };
            } else if (approxNumOfElements < 125) {
                return { present: `Editing lots of elements, this might take a while`, past: `Edited lots of elements` };
            } else {
                return { present: `Editing a heavy page`, past: `Edited a heavy page` };
            }
        } catch {
            return { present: `Trying to edit some elements`, past: `Tried to edit some elements` };
        }
    },
    searchDynamicData: step => {
        const { context } = (step as any).args ?? {};
        if (context) {
            return { present: `Searching dynamic data for ${context} context`, past: `Searched dynamic data for ${context} context` };
        }
        return { present: `Browsing available dynamic data`, past: `Browsed available dynamic data` };
    },
    editOrCreateDynamicGrid: step => {
        const { gridType } = (step as any).args ?? {};
        if (gridType) {
            return { present: `Configuring ${gridType} grid`, past: `Configured ${gridType} dynamic grid` };
        }
        return { present: `Configuring dynamic grid`, past: `Configured dynamic grid` };
    },
    editDesign: _ => {
        return randomDoingSomeDesignTexts[Math.floor(Math.random() * randomDoingSomeDesignTexts.length)];
    }
};

export const randomThinkingTexts: stepTextToShow[] = [
    { present: "Thinking", past: "Thought" },
    { present: "Processing", past: "Thought about it" },
    { present: "Let me think", past: "Thought about it" },
    { present: "Let me process", past: "Processed request" },
    { present: "Let me analyze", past: "Analyzed request" },
    { present: "Let me think", past: "Thought about request" }
];


export const randomDoingSomeDesignTexts: stepTextToShow[] = [
    { present: "Doing some design", past: "Did some design" },
    { present: "I am going to edit some elements", past: "I edited some elements" },
    { present: "Playing around with some elements", past: "Played around with some elements" },
    { present: "Making some design tweaks", past: "Made some design tweaks" },
    { present: "Adjusting a few elements", past: "Adjusted a few elements" },
    { present: "Working on some layouts", past: "Worked on some layouts" },
    { present: "Experimenting with the design", past: "Experimented with the design" },
    { present: "Customizing a few things", past: "Customized a few things" },
    { present: "Refining the design a bit", past: "Refined the design a bit" },
  ];
  